/**
 * SafetyDashboardPage - Comprehensive safety monitoring (DRR-236 Track B)
 *
 * Surfaces executiveSafetyMetrics and getCriticalEvents to frontend.
 * Provides real-time safety metrics, critical event list, and emergency access panel.
 *
 * LIFE-CRITICAL: This dashboard aggregates T3 QAPI-protected safety data.
 * Requires: safety:read scope (DRR-102)
 *
 * Route: /admin/safety-dashboard
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Group,
  Badge,
  Text,
  ThemeIcon,
  Grid,
  Card,
  Stack,
  RingProgress,
  Table,
  Alert,
  Loader,
  ActionIcon,
  Tooltip,
  Paper,
  Center,
  Tabs,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconRefresh,
  IconClock,
  IconActivity,
  IconFlame,
  IconCheck,
  IconX,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useAuth, useTenantId } from '@/auth/AuthContext';
import { BreakGlassPanel } from '@/components/emergency/BreakGlassPanel';

/**
 * Inline GraphQL queries (codegen Documents not generating for this file)
 */
const EXECUTIVE_SAFETY_METRICS_QUERY = gql`
  query ExecutiveSafetyMetrics($tenant: String!, $timeWindowSecs: Int) {
    executiveSafetyMetrics(input: {
      tenant: $tenant,
      timeWindowSecs: $timeWindowSecs,
      includeDeprecated: false
    }) {
      overallSafetyScore
      auditTrailIntegrity
      emergencyResponseTime
      riskLevel
      regulatoryCompliance
      lastUpdated
      trend
      domainMetrics {
        domain
        name
        value
        target
        trend
        status
      }
    }
  }
`;

const GET_CRITICAL_EVENTS_QUERY = gql`
  query GetCriticalEvents($tenantId: String!, $limit: Int) {
    getCriticalEvents(tenantId: $tenantId, limit: $limit) {
      id
      eventName
      eventType
      entityType
      severity
      category
      changeSummary
      metadata
      performedAt
      isActive
      tags
    }
  }
`;

/**
 * Get color based on safety score
 */
function getScoreColor(score: number): string {
  if (score >= 98) return 'green';
  if (score >= 90) return 'yellow';
  if (score >= 80) return 'orange';
  return 'red';
}

/**
 * Get color based on risk level
 */
function getRiskColor(riskLevel: string): string {
  switch (riskLevel?.toLowerCase()) {
    case 'low':
      return 'green';
    case 'medium':
      return 'yellow';
    case 'high':
      return 'orange';
    case 'critical':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get color based on metric status
 */
function getStatusColor(status: string): string {
  switch (status) {
    case 'OnTarget':
    case 'AboveTarget':
      return 'green';
    case 'BelowTarget':
      return 'yellow';
    case 'AtRisk':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string | number): string {
  const date = new Date(typeof timestamp === 'number' ? timestamp : parseInt(timestamp, 10));
  return date.toLocaleString();
}

interface DomainMetric {
  domain: string;
  name: string;
  value: number;
  target: number;
  trend: number;
  status: string;
}

interface SafetyMetrics {
  overallSafetyScore: number;
  auditTrailIntegrity: number;
  emergencyResponseTime: number;
  riskLevel: string;
  regulatoryCompliance: number;
  lastUpdated: string | number;
  trend: string;
  domainMetrics: DomainMetric[];
}

interface CriticalEvent {
  id: string;
  eventName: string;
  eventType: string;
  entityType: string;
  severity: string;
  category: string;
  changeSummary: string;
  metadata: Record<string, unknown>;
  performedAt: string | number;
  isActive: boolean;
  tags: string[];
}

export function SafetyDashboardPage() {
  const { tenant } = useAuth();
  const tenantId = useTenantId();
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  // Query executive safety metrics
  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<{ executiveSafetyMetrics: SafetyMetrics }>(EXECUTIVE_SAFETY_METRICS_QUERY, {
    variables: {
      tenant: tenantId,
      timeWindowSecs: 86400, // 24 hours
    },
    skip: !tenantId,
    pollInterval: 60000, // Poll every minute
  });

  // Query critical events
  const {
    data: eventsData,
    loading: eventsLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useQuery<{ getCriticalEvents: CriticalEvent[] }>(GET_CRITICAL_EVENTS_QUERY, {
    variables: {
      tenantId,
      limit: 50,
    },
    skip: !tenantId,
    pollInterval: 30000, // Poll every 30 seconds for critical events
  });

  const metrics = metricsData?.executiveSafetyMetrics;
  const criticalEvents = eventsData?.getCriticalEvents ?? [];

  const handleRefresh = () => {
    refetchMetrics();
    refetchEvents();
  };

  if (!tenantId) {
    return (
      <Container size="xl" py="md">
        <Alert color="red" title="Authentication Required">
          Please log in to access the safety dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="red">
            <IconShieldCheck size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Safety Dashboard</Title>
            <Text size="sm" c="dimmed">
              Real-time safety metrics, critical events, and emergency access
            </Text>
          </div>
        </Group>
        <Group>
          {tenant && (
            <Badge variant="light" color="gray">
              {tenant.name}
            </Badge>
          )}
          <Tooltip label="Refresh all data">
            <ActionIcon variant="light" onClick={handleRefresh}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value="critical"
            leftSection={<IconFlame size={16} />}
            rightSection={
              criticalEvents.length > 0 && (
                <Badge size="sm" color="red" variant="filled">
                  {criticalEvents.length}
                </Badge>
              )
            }
          >
            Critical Events
          </Tabs.Tab>
          <Tabs.Tab value="emergency" leftSection={<IconAlertTriangle size={16} />}>
            Emergency Access
          </Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview">
          {metricsLoading && (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          )}

          {metricsError && (
            <Alert color="red" title="Error Loading Metrics" mb="md">
              {metricsError.message}
            </Alert>
          )}

          {metrics && (
            <Grid>
              {/* Overall Safety Score */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card shadow="sm" padding="lg" withBorder>
                  <Stack align="center">
                    <RingProgress
                      size={150}
                      thickness={12}
                      sections={[
                        {
                          value: metrics.overallSafetyScore,
                          color: getScoreColor(metrics.overallSafetyScore),
                        },
                      ]}
                      label={
                        <Center>
                          <Stack gap={0} align="center">
                            <Text fw={700} size="xl">
                              {metrics.overallSafetyScore.toFixed(1)}%
                            </Text>
                            <Text size="xs" c="dimmed">
                              Safety Score
                            </Text>
                          </Stack>
                        </Center>
                      }
                    />
                    <Badge color={getRiskColor(metrics.riskLevel)} size="lg">
                      {metrics.riskLevel} Risk
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Key Metrics */}
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Grid>
                  <Grid.Col span={6}>
                    <Paper shadow="xs" p="md" withBorder>
                      <Group justify="space-between">
                        <div>
                          <Text size="xs" c="dimmed">
                            Audit Trail Integrity
                          </Text>
                          <Text fw={700} size="xl">
                            {metrics.auditTrailIntegrity.toFixed(1)}%
                          </Text>
                        </div>
                        <ThemeIcon
                          size="lg"
                          variant="light"
                          color={metrics.auditTrailIntegrity >= 100 ? 'green' : 'orange'}
                        >
                          <IconCheck size={20} />
                        </ThemeIcon>
                      </Group>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={6}>
                    <Paper shadow="xs" p="md" withBorder>
                      <Group justify="space-between">
                        <div>
                          <Text size="xs" c="dimmed">
                            Emergency Response
                          </Text>
                          <Text fw={700} size="xl">
                            {metrics.emergencyResponseTime}ms
                          </Text>
                        </div>
                        <ThemeIcon
                          size="lg"
                          variant="light"
                          color={metrics.emergencyResponseTime < 5000 ? 'green' : 'orange'}
                        >
                          <IconClock size={20} />
                        </ThemeIcon>
                      </Group>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={12}>
                    <Paper shadow="xs" p="md" withBorder>
                      <Text size="sm" fw={500} mb="sm">
                        Domain Metrics
                      </Text>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Domain</Table.Th>
                            <Table.Th>Metric</Table.Th>
                            <Table.Th>Value</Table.Th>
                            <Table.Th>Target</Table.Th>
                            <Table.Th>Trend</Table.Th>
                            <Table.Th>Status</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {metrics.domainMetrics.map((dm) => (
                            <Table.Tr key={`${dm.domain}-${dm.name}`}>
                              <Table.Td>{dm.domain}</Table.Td>
                              <Table.Td>{dm.name}</Table.Td>
                              <Table.Td>
                                <Badge color={getScoreColor(dm.value)}>{dm.value.toFixed(1)}%</Badge>
                              </Table.Td>
                              <Table.Td>{dm.target.toFixed(1)}%</Table.Td>
                              <Table.Td>
                                <Group gap="xs">
                                  {dm.trend > 0 ? (
                                    <IconTrendingUp size={16} color="green" />
                                  ) : dm.trend < 0 ? (
                                    <IconTrendingDown size={16} color="red" />
                                  ) : (
                                    <IconMinus size={16} color="gray" />
                                  )}
                                  <Text size="sm" c={dm.trend > 0 ? 'green' : dm.trend < 0 ? 'red' : 'gray'}>
                                    {dm.trend > 0 ? '+' : ''}{dm.trend.toFixed(2)}%
                                  </Text>
                                </Group>
                              </Table.Td>
                              <Table.Td>
                                <Badge color={getStatusColor(dm.status)}>{dm.status}</Badge>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Paper>
                  </Grid.Col>

                  <Grid.Col span={12}>
                    <Text size="xs" c="dimmed" ta="right">
                      Last updated: {formatTimestamp(metrics.lastUpdated)}
                    </Text>
                  </Grid.Col>
                </Grid>
              </Grid.Col>
            </Grid>
          )}
        </Tabs.Panel>

        {/* Critical Events Tab */}
        <Tabs.Panel value="critical">
          {eventsLoading && (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          )}

          {eventsError && (
            <Alert color="red" title="Error Loading Events" mb="md">
              {eventsError.message}
            </Alert>
          )}

          {criticalEvents.length === 0 && !eventsLoading && (
            <Alert color="green" title="No Critical Events" icon={<IconCheck size={16} />}>
              There are no critical events at this time.
            </Alert>
          )}

          {criticalEvents.length > 0 && (
            <Card shadow="sm" padding="lg" withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Time</Table.Th>
                    <Table.Th>Event</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Entity</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {criticalEvents.map((event) => (
                    <Table.Tr key={event.id}>
                      <Table.Td>
                        <Text size="sm">{formatTimestamp(event.performedAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{event.eventName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="red">{event.eventType}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2}>
                          {event.changeSummary}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{event.entityType}</Text>
                      </Table.Td>
                      <Table.Td>
                        {event.isActive ? (
                          <Badge color="red" leftSection={<IconX size={12} />}>
                            Active
                          </Badge>
                        ) : (
                          <Badge color="green" leftSection={<IconCheck size={12} />}>
                            Resolved
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          )}
        </Tabs.Panel>

        {/* Emergency Access Tab */}
        <Tabs.Panel value="emergency">
          <BreakGlassPanel />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default SafetyDashboardPage;
